<template>
  <!-- prettier-ignore -->
  <div
    class="query"
  >
    <basic-text-input
      ref="inputRef"
      v-model="selectedLabel"
      :container-style="{ 'anchor-name': `--${dropdownId}` }"
      manual-validation
      :name="$props.name"
      :disabled="$props.disabled"
      :label="$props.label"
      :required="$props.required"
      :size="$props.size"
      :supporting-text="$props.supportingText"
      :v="$props.v"
      @clear="doClear"
      @keydown="onKeyDown"
      @input.stop="search"
      @focus="doFocus"
    >
      <template #leading>
        <slot name="leading" />
      </template>

      <template #trailing>
        <the-icon
          v-if="isLoading"
          class="fa-spin"
          art="duo"
          name="spinner-third"
        />
      </template>
    </basic-text-input>

    <basic-dropdown-menu
      :id="dropdownId"
      ref="dropdownRef"
      manual-focus
      name="query-dropdown"
      :options="results"
      :selected="model"
      :size="$props.size"
      @close="onClose"
      @select="selectItem"
    />
  </div>
</template>

<script setup>
  import { nextTick, onMounted, reactive, ref, useTemplateRef, watch } from 'vue'
  import { v4 as uuidv4 } from 'uuid'
  import { debounce } from 'lodash'

  import { ReactivityUtil } from '@shared/utils/Reactivity'

  import BasicDropdownMenu from '@shared/components/Basic/DropdownMenu'
  import BasicTextInput from '@shared/components/Basic/TextInput'

  import { DEFAULT_FORM_DEBOUNCE_TIME } from '@shared/config/constants'

  // INIT
  const model = defineModel({ type: [Object, String] })
  const props = defineProps({
    disabled: {
      type: Boolean,
      default: false,
    },

    hasTrailing: {
      type: Boolean,
      default: false,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    label: {
      type: String,
      required: true,
    },

    labelKey: {
      type: String,
      default: 'label',
    },

    name: {
      type: String,
      required: true,
    },

    options: {
      type: Array,
      default: () => [],
    },

    required: {
      type: Boolean,
      default: false,
    },

    size: {
      validator(value) {
        return ['sm', 'md'].includes(value)
      },
      default: 'sm',
    },

    supportingText: {
      type: String,
      default: undefined,
    },

    v: {
      type: Object,
      default: () => {},
    },

    value: {
      type: Object,
      default: () => {},
    },
  })

  // DATA
  const KEY = uuidv4()
  const dropdownId = `dropdown-${KEY}`
  const dropdownRef = useTemplateRef('dropdownRef')
  const inputRef = useTemplateRef('inputRef')
  const selectedLabel = ref(null)
  const results = reactive([...props.options])

  // METHODS
  function doClear() {
    model.value = null
    ReactivityUtil.reAssign(results, props.options)
  }

  function filterResults(searchTerm) {
    if (!searchTerm) return props.options

    const sanitizedSearchTerm = searchTerm.toString().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const searchRegex = new RegExp(`^${sanitizedSearchTerm}`, 'i')

    const filteredResults = props.options.filter(option => {
      if (Number.isInteger(option.label)) {
        return option.label.toString().match(searchRegex)
      } else {
        return option.label.match(searchRegex)
      }
    })

    return filteredResults
  }

  function doFocus() {
    
    _search()

    dropdownRef.value.open()
  }

  async function onClose() {
    await nextTick()

    if (!props.flatLabel && !model.value) {
      selectedLabel.value = null
    }
  }

  /**
   * handle keydown-events to set focus to opened dropdown menu
   */
  function onKeyDown(event) {
    if (dropdownRef.value.isOpen()) {
      switch (event.code) {
        case 'Tab':
        case 'ArrowUp':
        case 'ArrowDown':
          dropdownRef.value.setFocus()
          event.preventDefault()
          break
      }
    }
  }

  /**
   * select item and then set focus on the input-field (to restore tabbing order)
   */
  function selectItem(item) {
    model.value = item.value
    dropdownRef.value.close()

    if (!props.manualValidation) props.v?.$validate()
  }

  const search = debounce(_search, DEFAULT_FORM_DEBOUNCE_TIME)
  function _search() {
    model.value = null
    props.v?.$reset()

    const filteredResults = filterResults(selectedLabel.value)

    ReactivityUtil.reAssign(results, filteredResults)
  }

  // WATCHERS
  watch(
    model,
    value => {
      if (!value) return
      selectedLabel.value = props.options.find(o => o.value === value).label
    },
    { immediate: true, deep: true }
  )

  watch(results, values => {
    if (values.length > 0) {
      dropdownRef.value.open()
    } else {
      dropdownRef.value.close()
    }
  })

  // LIFECYCLE HOOKS
  onMounted(() => {
    if (props.value && props.value[props.labelKey]) {
      selectedLabel.value = props.value[props.labelKey]
    }
  })
</script>

<style scoped>
  .query {
    width: 100%;
  }

  .location {
    position: relative;

    .icon {
      color: var(--on-surface);
    }
  }
</style>
